import React from 'react';
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import BannerDisplay from "./BannerDisplay";
import FileUpload from "../../../components/FileUpload";
import {getGradeName} from "../../../constants";
import PreImage from "../../../components/PreImage";
import FeatherIcon from "feather-icons-react";
import { useTheme } from "../../../contexts/ThemeContext";
import { parseDate } from "../../../utils/parseDate";

// Same date+time formatting as TournamentHero.js's own copy (date only,
// via the hero's single "Date" row, never showed end_date/time at all) -
// kept as a local copy rather than a shared import, same reasoning as
// that file's own getTournamentStatus duplication comment.
const formatDateTime = (value) => {
    const d = parseDate(value);
    if (!d) return "";
    return d.toLocaleString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const TournamentInformation = ({ tournament, loading }) => {
    const { t } = useTranslation();
    const { compactTable } = useTheme();

    if (!tournament && !loading) return null;

    const data = tournament || {};

    // location_image (the venue/base's own stock photo) fills in for a
    // tournament that hasn't uploaded its own banner - reads fine in the
    // full card layout, but in Compact table mode showing someone else's
    // location photo as if it were this tournament's own banner reads as
    // misleading rather than helpful, so it's skipped there: no banner
    // means no image at all, not a substitute one.
    const hasOwnImage = !!data.image;
    const showLocationFallback = !compactTable && !hasOwnImage && !!data.location_image;
    const bannerSrc = data.image?.url || data.image || (showLocationFallback ? data.location_image : null);
    const bannerWidth = hasOwnImage ? data.width : (showLocationFallback ? data.location_width : data.width);
    const bannerHeight = hasOwnImage ? data.height : (showLocationFallback ? data.location_height : data.height);

    return (
        <div id="information-block" className="tournament-form" style={{ marginTop: "15px" }}>
            <div className="item-container no-cursor">
                <div style={{position: "relative", marginBottom: "10px"}} className="no-laptop">
                    {
                        bannerSrc ?
                            <PreImage
                                src={bannerSrc}
                                width={bannerWidth}
                                height={bannerHeight}
                            />
                            :
                                <div className="banner-preview non-authorized" style={{minHeight: "200px"}}>
                                    <FeatherIcon icon="camera-off" />
                                </div>
                    }
                </div>
                {/* Club/Name/Location/Start Date/End Date dropped here - this
                    read-only view (public visitors only; TournamentInformationForm's
                    own editable version above this one is unaffected) sits directly
                    below the hero, which already shows all of these (name, club+link,
                    date, location+link), so repeating them here was pure duplication.

                    Grouped into titled sections (same idea as the "Format & rules" /
                    "Prizes & award" / "Contacts & organizers" split in the redesign
                    mockup) instead of one flat 11-row table - each group keeps using
                    .tournament_table for its own rows, so Compact Table mode's
                    existing #information-block .tournament_table overrides (zebra
                    stripe, edge-to-edge padding, etc.) still apply unchanged. No
                    card border around each group (index.css's own .info-section-title
                    comment has the reasoning) - just a menu-label-style title above
                    each group's rows. */}
                <div className="info-section-list">
                    <div>
                        <table className="tournament_table">
                            <tbody>
                            <tr>
                                <td>{t("Start Date")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : formatDateTime(data.start_date) || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("End Date")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : formatDateTime(data.end_date) || ''}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <table className="tournament_table">
                            <tbody>
                            <tr>
                                <td>{t("Format")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) :
                                                data.grade
                                                    ? `${getGradeName(data.grade)} · ${
                                                        data.gender ? t(`gender.${data.gender}`) : ''
                                                    }`
                                                    : ''
                                            }
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Level")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.format || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Tournament regulations")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.regulations
                                        ? (/^https?:\/\//i.test(data.regulations)
                                            ? <a href={data.regulations} target="_blank" rel="noopener noreferrer" className="tournament-header-club-link">
                                                <span className="tournament-header-club-name">{t("Tournament regulations")}</span>
                                                <FeatherIcon icon="external-link" size={11} />
                                            </a>
                                            : data.regulations)
                                        : ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Age")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) :  data.age || ''
                                    }
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Drawing")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.sort || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Number of courts")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.courts_count || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Court surface")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.court_surface || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Official Ball")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.official_ball || ''}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <table className="tournament_table">
                            <tbody>
                            <tr>
                                <td>{t("Money")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.money || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Prize Fund")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.prize_fund || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Award")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.award || ''}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <table className="tournament_table">
                            <tbody>
                            <tr>
                                <td>{t("Organizers and sponsors")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.participation || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Head referee")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.tasks || ''}
                                </td>
                            </tr>
                            <tr>
                                <td>{t("Contact data")}</td>
                                <td>
                                    {loading ? (
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    ) : data.phone || ''}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="sidebar-wrapper">
                <div className="no-mobile">
                    {loading ? (
                        <Skeleton height={200} width={'100%'} baseColor={'#f8f7fa'} />
                    ) :
                        <div className="content_page banner-container">
                            <label>{t("Image")}</label>
                            <BannerDisplay
                                image={bannerSrc}
                                width={bannerWidth}
                                height={bannerHeight}
                                is_authorized={false}
                            />
                        </div>
                    }
                </div>
                <div>
                    <FileUpload
                        id={data.id}
                        file={data.file}
                        loading={loading}
                        is_authorized={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default TournamentInformation;
